"use client";

import { useCallback } from "react";

import { useRouter } from "next/navigation";

import { toast } from "sonner";

import { RelatedProductCard } from "@/components/product/RelatedProductCard";
import { TextArrowButton } from "@/components/shared/TextArrowButton";
import { CarouselArrow } from "@/components/ui/CarouselArrow";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { type CuratedQuery, navigateToCuratedSearch } from "@/lib/curated-queries";
import {
  PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE,
  buildCanonicalProductPathFromRecord,
  warnMissingCanonicalProductPath,
} from "@/lib/product-url";
import { type Product, getProductAnyId } from "@/lib/product-utils";

const DISPLAY_LIMIT = 12;

interface CuratedSearchRailProps {
  query: CuratedQuery;
  products: Product[];
}

function getProductRailItemKey(product: Product, index: number): string {
  const id = getProductAnyId(product);
  return id !== "unknown" ? id : `curated-${index}`;
}

export function CuratedSearchRail({ query, products }: CuratedSearchRailProps) {
  const router = useRouter();
  const displayProducts = products.slice(0, DISPLAY_LIMIT);

  const navigateToProduct = useCallback(
    (product: Product) => {
      const productId = getProductAnyId(product);
      if (productId !== "unknown") {
        try {
          localStorage.setItem(`product-${productId.toLowerCase()}`, JSON.stringify(product));
        } catch {
          // Storage may be full; navigation still works.
        }
      }
      const href = buildCanonicalProductPathFromRecord(
        product as unknown as Record<string, unknown>,
        productId
      );
      if (!href) {
        warnMissingCanonicalProductPath("CuratedSearchRail", productId);
        toast.error(PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE);
        return;
      }
      router.push(href);
    },
    [router]
  );

  const {
    scrollRef: desktopRef,
    canScrollLeft: canLeftDesktop,
    canScrollRight: canRightDesktop,
    scrollLeft: scrollLeftDesktop,
    scrollRight: scrollRightDesktop,
  } = useHorizontalScroll({
    threshold: 1,
    scrollAmount: "viewport",
    viewportMultiplier: 0.75,
    deps: [displayProducts.length],
  });

  const {
    scrollRef: mobileRef,
    canScrollLeft: canLeftMobile,
    canScrollRight: canRightMobile,
    scrollLeft: scrollLeftMobile,
    scrollRight: scrollRightMobile,
  } = useHorizontalScroll({
    threshold: 1,
    scrollAmount: "viewport",
    viewportMultiplier: 0.75,
    deps: [displayProducts.length],
  });

  if (displayProducts.length === 0) return null;

  return (
    <section className="w-full">
      <div className="relative hidden lg:block">
        <div className="flex items-center justify-between px-8 pb-4">
          <button
            className="text-xl font-medium text-gray-900 transition-opacity hover:opacity-70"
            onClick={() => navigateToCuratedSearch(router, query)}
          >
            {query.title}
          </button>
          <TextArrowButton label="See all" onClick={() => navigateToCuratedSearch(router, query)} />
        </div>
        {canLeftDesktop && (
          <CarouselArrow
            direction="prev"
            onClick={scrollLeftDesktop}
            size="sm"
            className="absolute left-2 top-[55%] z-10 -translate-y-1/2"
          />
        )}
        {canRightDesktop && (
          <CarouselArrow
            direction="next"
            onClick={scrollRightDesktop}
            size="sm"
            className="absolute right-2 top-[55%] z-10 -translate-y-1/2"
          />
        )}
        <div
          ref={desktopRef}
          className="flex overflow-x-auto pb-8 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {displayProducts.map((product, index) => (
            <div
              key={getProductRailItemKey(product, index)}
              className="w-[calc(100%/8)] flex-shrink-0"
            >
              <RelatedProductCard
                product={product}
                onClick={() => navigateToProduct(product)}
                variant="editorial"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="relative px-6 py-6 lg:hidden">
        <div className="mb-6 flex items-center justify-between">
          <button
            className="text-lg font-medium text-gray-900 transition-opacity hover:opacity-70"
            onClick={() => navigateToCuratedSearch(router, query)}
          >
            {query.title}
          </button>
          <TextArrowButton label="See all" onClick={() => navigateToCuratedSearch(router, query)} />
        </div>
        {canLeftMobile && (
          <CarouselArrow
            direction="prev"
            onClick={scrollLeftMobile}
            size="sm"
            className="absolute -left-2 top-1/3 z-10 -translate-y-1/2"
          />
        )}
        {canRightMobile && (
          <CarouselArrow
            direction="next"
            onClick={scrollRightMobile}
            size="sm"
            className="absolute -right-2 top-1/3 z-10 -translate-y-1/2"
          />
        )}
        <div
          ref={mobileRef}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4"
          style={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {displayProducts.map((product, index) => (
            <div
              key={getProductRailItemKey(product, index)}
              className="w-20 flex-shrink-0 snap-start"
            >
              <RelatedProductCard product={product} onClick={() => navigateToProduct(product)} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
